import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import { Helmet } from 'react-helmet';
import {
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Toolbar,
  Typography,
  Avatar,
  Container,
  useMediaQuery,
  ThemeProvider,
} from '@mui/material';
import { styled, alpha, createTheme, lighten } from '@mui/material/styles';
import PersonIcon from '@mui/icons-material/Person';
import StarsIcon from '@mui/icons-material/Stars';

import HomeHeader from '../../Components/HomeHeader';
import { darkTheme } from '../../Utils/baseTheme';
import defaultPfp from '../../Components/globalAssets/ppfLogo.png';
import API from '../../API';
import useAuth from '../../Hooks/use-auth';
import { useCreatorAuth } from '../../Hooks/creator-use-auth';
import routes from '../../Config/routes';

/*==============================================================================
  Styled components
==============================================================================*/
const NeonGlow = styled('div')(({ theme }) => ({
  position: 'absolute',
  width: '120%',
  height: '120%',
  background: `linear-gradient(45deg, 
    ${alpha(theme.palette.primary.main, 0.4)} 20%,
    ${alpha(theme.palette.primary.main, 0.2)} 50%,
    ${alpha(theme.palette.primary.main, 0.4)} 80%)`,
  filter: 'blur(80px)',
  zIndex: -1,
  opacity: 0.6,
}));

const JoinButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(145deg,
    ${theme.palette.primary.main} 0%,
    ${lighten(theme.palette.primary.main, 0.2)} 100%)`,
  color: theme.palette.common.white,
  borderRadius: '16px',
  padding: theme.spacing(2, 6),
  fontWeight: 700,
  letterSpacing: '1px',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '200%',
    height: '100%',
    background: `linear-gradient(
      120deg,
      transparent,
      ${alpha(theme.palette.common.white, 0.2)},
      transparent
    )`,
    transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.4)}`,
    '&:before': {
      left: '100%',
    },
  },
}));

const HeroSection = styled('section')(({ theme, image }) => ({
  position: 'relative',
  height: '60vh',
  minHeight: '400px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: `linear-gradient(45deg, 
    ${alpha(theme.palette.common.black, 0.9)} 30%,
    ${alpha(theme.palette.common.black, 0.6)}),
    url("${image}")`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  borderRadius: '24px',
  overflow: 'hidden',
  margin: theme.spacing(4, 0),
  boxShadow: `0 16px 48px ${alpha(theme.palette.common.black, 0.6)}`,
}));

const HeroContent = styled(Box)(({ theme }) => ({
  position: 'relative',
  textAlign: 'center',
  zIndex: 1,
  padding: theme.spacing(4),
  backdropFilter: 'blur(8px)',
  backgroundColor: alpha(theme.palette.common.black, 0.4),
  borderRadius: '16px',
  border: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  position: 'relative',
  paddingBottom: theme.spacing(2),
  '&:after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    width: '80px',
    height: '3px',
    background: `linear-gradient(90deg, 
      ${theme.palette.primary.main} 0%,
      ${lighten(theme.palette.primary.main, 0.2)} 100%)`,
    borderRadius: '2px',
  },
}));

const ParticipantCard = styled(Card)(({ theme }) => ({
  background: alpha(theme.palette.common.white, 0.05),
  backdropFilter: 'blur(8px)',
  border: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
  borderRadius: '16px',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: `0 12px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
  },
}));

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: 64,
  height: 64,
  border: `2px solid ${theme.palette.primary.main}`,
  boxShadow: `0 0 16px ${alpha(theme.palette.primary.main, 0.4)}`,
}));

export default function JoinConversationPage() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const { isAuthorized } = useAuth();
  const { creatorToken } = useCreatorAuth();
  const isCreatorAuthorized = Boolean(creatorToken);
  const [openAuth, setOpenAuth] = useState(false);
  const isMobile = useMediaQuery(darkTheme.breakpoints.down('sm'));

  // Fetch conversation
  const { data: convo, isLoading, isError, error } = useQuery(
    ['publicConversation', conversationId],
    () => API.conversations.getPublic(conversationId),
    { enabled: !!conversationId }
  );

  // Base theme color from conversation
  const themeColor = convo?.theme;

  // Create a responsive theme, guarding against invalid themeColor types
  const convTheme = useMemo(() => {
    const hasValidColor = typeof themeColor === 'string' && themeColor.trim() !== '';
    const primaryMain = hasValidColor ? themeColor : darkTheme.palette.primary.main;
    const secondaryMain = hasValidColor
      ? lighten(primaryMain, 0.2)
      : darkTheme.palette.secondary.main;

    return createTheme(darkTheme, {
      palette: {
        primary: { main: primaryMain },
        secondary: { main: secondaryMain },
      },
    });
  }, [themeColor]);

  // Join mutation
  // ─── Join mutation ──────────────────────────────────────────────────────────
  const joinMutation = useMutation(() => API.conversations.join(conversationId), {
    onSuccess: (data) => {
      // prefer the ID returned from the API in case the URL param was slug-based
      const cid        = data?.conversation_id ?? conversationId;
      const baseRoute  = isCreatorAuthorized
        ? '/creatorconnect/convos'   // creators
        : '/convos';                 // normal users

      navigate(`${baseRoute}?conversation=${cid}`);
    },
    onError: (err) => alert(err?.message || 'Error joining conversation'),
  });


  const handleJoin = () => {
    if (!isAuthorized && !isCreatorAuthorized) setOpenAuth(true);
    else joinMutation.mutate();
  };

  // Derived lists
  const users = useMemo(() => convo?.participants || [], [convo]);
  const globals = useMemo(() => convo?.global_participants || [], [convo]);

  // Loading / error states
  if (isLoading) {
    return (
      <Backdrop open sx={{ background: 'rgba(0, 0, 0, 0.8)' }}>
        <CircularProgress sx={{ color: convTheme.palette.primary.main }} />
      </Backdrop>
    );
  }

  if (isError) {
    return (
      <Container sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="h5" color="error">
          {error?.message || 'Failed to load conversation'}
        </Typography>
      </Container>
    );
  }

  if (!convo) {
    return (
      <Container sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="h5">🚫 Conversation Not Found</Typography>
      </Container>
    );
  }

  const { display_name, description, header_media_url, creator } = convo;

  return (
    <ThemeProvider theme={convTheme}>
      <CssBaseline />
      <HomeHeader />
      <Toolbar />

      <Container maxWidth="lg" sx={{ my: 6 }}>
        <Helmet><title>{display_name || 'Join Conversation'}</title></Helmet>

        {/* Hero Section */}
        <HeroSection image={header_media_url || defaultPfp}>
          <NeonGlow />
          <HeroContent>
            <Typography
              variant={isMobile ? 'h4' : 'h2'}
              gutterBottom
              sx={{
                background: `linear-gradient(45deg, 
                  ${convTheme.palette.primary.main} 30%,
                  ${convTheme.palette.secondary.main} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontWeight: 800,
                letterSpacing: '-0.5px',
              }}
            >
              {display_name}
            </Typography>

            {description && (
              <Typography
                variant="h6"
                sx={{
                  color: alpha(convTheme.palette.common.white, 0.9),
                  maxWidth: '800px',
                  margin: '0 auto',
                  mb: 4,
                }}
              >
                {description}
              </Typography>
            )}

            <JoinButton
              onClick={handleJoin}
              disabled={joinMutation.isLoading}
            >
              {joinMutation.isLoading ? (
                <CircularProgress size={24} sx={{ color: 'white' }} />
              ) : (
                'Join the Conversation'
              )}
            </JoinButton>
          </HeroContent>
        </HeroSection>

        {/* Hosted By */}
        {creator && (
          <Box sx={{ mt: 8, textAlign: 'center' }}>
            <SectionTitle variant="h5" sx={{ color: 'common.white', mb: 4 }}>
              Hosted By
            </SectionTitle>
            <ParticipantCard sx={{ mx: 'auto', maxWidth: 400 }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                {creator.avatar ? (
                  <StyledAvatar src={creator.avatar} />
                ) : (
                  <StyledAvatar>
                    <StarsIcon sx={{ fontSize: 32 }} />
                  </StyledAvatar>
                )}
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {creator.display_name || creator.username}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Conversation Host
                  </Typography>
                </Box>
              </CardContent>
            </ParticipantCard>
          </Box>
        )}

        {/* Participants & Global Members */}
        <Grid container spacing={4} sx={{ mt: 2 }}>
          {/* Participants */}
          <Grid item xs={12} md={6}>
            <SectionTitle variant="h5" sx={{ color: 'common.white', mb: 4 }}>
              Participants ({users.length})
            </SectionTitle>
            <Grid container spacing={3}>
              {users.length === 0 ? (
                <Grid item xs={12}>
                  <Typography
                    variant="body1"
                    sx={{ color: 'text.secondary', textAlign: 'center' }}
                  >
                    Be the first to join!
                  </Typography>
                </Grid>
              ) : (
                users.map(u => (
                  <Grid key={u.id} item xs={12} sm={6}>
                    <ParticipantCard>
                      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <StyledAvatar src={u.avatar || defaultPfp} />
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {u.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            Participant
                          </Typography>
                        </Box>
                      </CardContent>
                    </ParticipantCard>
                  </Grid>
                ))
              )}
            </Grid>
          </Grid>

          {/* Global Members */}
          <Grid item xs={12} md={6}>
            <SectionTitle variant="h5" sx={{ color: 'common.white', mb: 4 }}>
              Global Members ({globals.length})
            </SectionTitle>
            <Grid container spacing={3}>
              {globals.length === 0 ? (
                <Grid item xs={12}>
                  <Typography
                    variant="body1"
                    sx={{ color: 'text.secondary', textAlign: 'center' }}
                  >
                    Waiting for global members...
                  </Typography>
                </Grid>
              ) : (
                globals.map(g => (
                  <Grid key={g.id} item xs={12} sm={6}>
                    <ParticipantCard>
                      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <StyledAvatar src={g.avatar || defaultPfp} />
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {g.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: g.user_type === 'creator'
                                ? 'primary.main'
                                : 'text.secondary'
                            }}
                          >
                            {g.user_type.toUpperCase()}
                          </Typography>
                        </Box>
                      </CardContent>
                    </ParticipantCard>
                  </Grid>
                ))
              )}
            </Grid>
          </Grid>
        </Grid>
      </Container>

      {/* Auth Dialog */}
      <Dialog
        open={openAuth}
        onClose={() => setOpenAuth(false)}
        PaperProps={{
          sx: {
            background: alpha(convTheme.palette.background.default, 0.9),
            backdropFilter: 'blur(12px)',
            borderRadius: '16px',
            border: `1px solid ${alpha(convTheme.palette.common.white, 0.1)}`,
          }
        }}
      >
        <DialogTitle sx={{ color: 'common.white' }}>
          Join the Experience
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'text.secondary' }}>
            Sign in to access this exclusive conversation and connect with the community.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ gap: 2, p: 3 }}>
          <Button
            variant="outlined"
            onClick={() => navigate(routes.login)}
            sx={{
              flex: 1,
              color: 'primary.main',
              borderColor: 'primary.main',
              '&:hover': { borderColor: 'primary.light' }
            }}
          >
            Existing User
          </Button>
          <Button
            variant="contained"
            onClick={() => navigate(routes.onboard)}
            sx={{
              flex: 1,
              background: `linear-gradient(45deg, 
                ${convTheme.palette.primary.main} 0%,
                ${convTheme.palette.secondary.main} 100%)`,
              '&:hover': { opacity: 0.9 }
            }}
          >
            New Account
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
}